/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Custom Line Adjustment Renderer Component
 */
import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';
import { App, Button, Divider, InputNumber, Popover, Select, Table, Typography } from 'antd';
import { DeleteOne, Info, Plus } from '@icon-park/react';
import { useField, useForm } from '@formily/react';

import { GROUP_CODES, REASON_CODES } from 'app-constants/claims/reasonCodes';
import { renderAmount } from 'utils/claims/fhir';
import { schemaKeys } from 'utils/claims/adjudicationSchema';
import { MODAL_TYPES } from 'components/widgets/modal';

const groupOptions = _.map(GROUP_CODES, ({ code, display }) => ({ label: `${code} - ${display}`, value: code }));
const reasonOptions = _.map(REASON_CODES, ({ code }) => ({ label: code.toString(), value: code.toString() }));

export interface LineAdjustmentItem {
	group?: { value: string; label: string };
	reasonCode?: { value: string; label: string };
	amount?: number | null;
	key?: string;
}

const defaultNewItem: LineAdjustmentItem = {
	group: undefined,
	reasonCode: undefined,
	amount: null,
};

const CONFIRMATION_CONTENT = (
	<>
		<Typography.Paragraph>
			{'Adding line adjustments will override the Patient Responsibility and Adjusted Amount fields for this service line.'}
		</Typography.Paragraph>
		<Typography.Paragraph>{'Do you want to continue?'}</Typography.Paragraph>
	</>
);

const getServiceLineIndex = (path?: string | null): number | null => {
	if (!path) {
		return null;
	}

	const normalizedPath = path.replace(/\//g, '.');
	const segments = normalizedPath.split('.').filter(Boolean);
	const serviceLineSegmentIndex = segments.findIndex((segment) => segment === schemaKeys.serviceLines);
	if (serviceLineSegmentIndex === -1) {
		return null;
	}

	const indexSegment = segments[serviceLineSegmentIndex + 1];
	if (indexSegment === undefined) {
		return null;
	}

	const parsedIndex = _.toNumber(indexSegment);
	return Number.isNaN(parsedIndex) ? null : parsedIndex;
};

const CustomLineAdjustmentsRenderer: React.FC<{
	value?: LineAdjustmentItem[];
	onChange: (value: LineAdjustmentItem[]) => void;
	isEditing?: boolean;
}> = ({ value, onChange, isEditing = false }) => {
	const { modal } = App.useApp();
	const form = useForm() as PostPayment.IPostPaymentForm;
	const field = useField();
	const adjustments = _.isArray(value) ? value : [];
	const fieldPath = useMemo(() => field?.address?.toString() || field?.path?.toString() || '', [field?.address, field?.path]);

	const serviceLineIndex = getServiceLineIndex(field?.address?.toString() ?? '') ?? getServiceLineIndex(field?.path?.toString() ?? '');
	const patientResponsibilityPath =
		serviceLineIndex === null ? null : `${schemaKeys.serviceLines}.${serviceLineIndex}.${schemaKeys.patientResponsibility}`;
	const adjustedAmountPath =
		serviceLineIndex === null ? null : `${schemaKeys.serviceLines}.${serviceLineIndex}.${schemaKeys.adjustedAmount}`;

	const dataSource = adjustments.map((item, index) => ({
		...item,
		key: index.toString(),
	}));

	const updateFieldValue = useCallback(
		(updater: (currentValue: LineAdjustmentItem[]) => LineAdjustmentItem[]) => {
			const formValue = fieldPath ? form.getValuesIn(fieldPath) : undefined;
			const baseValue = _.isArray(formValue) ? formValue : adjustments;
			const currentValue = _.isArray(baseValue) ? baseValue.map((item) => ({ ...item })) : [];
			const newValue = updater(currentValue);

			if (fieldPath) {
				form.setValuesIn(fieldPath, newValue);
			}

			onChange?.(newValue);
		},
		[adjustments, fieldPath, form, onChange],
	);

	const handleGroupChange = useCallback(
		(index: number, groupValue: { value: string; label: string }) => {
			updateFieldValue((currentValue) => {
				const newValue = [...currentValue];
				newValue[index] = {
					...newValue[index],
					group: {
						value: groupValue.value,
						label: groupValue.label,
					},
					reasonCode: undefined,
				};
				return newValue;
			});
		},
		[updateFieldValue],
	);

	const handleReasonCodeChange = useCallback(
		(index: number, reasonCodeValue: { value: string; label: string }) => {
			updateFieldValue((currentValue) => {
				const newValue = [...currentValue];
				newValue[index] = {
					...newValue[index],
					reasonCode: {
						value: reasonCodeValue.value,
						label: reasonCodeValue.label,
					},
				};
				return newValue;
			});
		},
		[updateFieldValue],
	);

	const handleAmountChange = useCallback(
		(index: number, amountValue: number | null) => {
			updateFieldValue((currentValue) => {
				const newValue = [...currentValue];
				newValue[index] = {
					...newValue[index],
					amount: amountValue,
				};
				return newValue;
			});
		},
		[updateFieldValue],
	);

	const removeItem = useCallback(
		(index: number) => {
			updateFieldValue((currentValue) => currentValue.filter((__, i) => i !== index));
		},
		[updateFieldValue],
	);

	const needsConfirmation = useCallback(() => {
		if (!patientResponsibilityPath || !adjustedAmountPath) {
			return false;
		}

		const patientValue = _.toNumber(form.getValuesIn(patientResponsibilityPath) || 0);
		const adjustedValue = _.toNumber(form.getValuesIn(adjustedAmountPath) || 0);
		return patientValue !== 0 || adjustedValue !== 0;
	}, [adjustedAmountPath, form, patientResponsibilityPath]);

	const resetManualAmounts = useCallback(() => {
		const targetPaths = _.compact([patientResponsibilityPath, adjustedAmountPath]);
		targetPaths.forEach((path) => {
			form.setValuesIn(path, 0);
		});
	}, [adjustedAmountPath, form, patientResponsibilityPath]);

	const confirmAdjustmentAddition = useCallback(
		(onProceed: VoidFunction) => {
			if (!needsConfirmation()) {
				onProceed();
				return;
			}

			modal.confirm({
				...MODAL_TYPES.confirm,
				title: 'Confirm Changes',
				content: CONFIRMATION_CONTENT,
				onOk: () => {
					resetManualAmounts();
					onProceed();
				},
			});
		},
		[modal, needsConfirmation, resetManualAmounts],
	);

	const addItem = useCallback(() => {
		const performAddition = () => {
			updateFieldValue((currentValue) => [...currentValue, _.cloneDeep(defaultNewItem)]);
		};

		const additionHandler = form?.handleLineAdjudicationAddition;

		if (additionHandler && fieldPath) {
			additionHandler(performAddition, { linePath: fieldPath });
			return;
		}

		confirmAdjustmentAddition(performAddition);
	}, [confirmAdjustmentAddition, fieldPath, form, updateFieldValue]);

	const columns = useMemo(
		() =>
			[
				{
					title: 'Group',
					dataIndex: 'group',
					key: 'group',
					width: '33%',
					render: (group: { value: string; label: string }, __: AnyType, index: number) =>
						isEditing ? (
							<Select
								value={group}
								labelInValue
								onChange={(newValue: { value: string; label: string }) => handleGroupChange(index, newValue)}
								options={groupOptions}
								placeholder='Select group'
								style={{ width: 'calc(100% - 30px)' }}
							/>
						) : (
							<Typography.Text>{group ? `${group.value} - ${group.label}` : ''}</Typography.Text>
						),
				},
				{
					title: 'Reason Code',
					dataIndex: 'reasonCode',
					key: 'reasonCode',
					flex: 2,
					width: '33%',
					render: (reasonCode: { value: string; label: string }, record: AnyType, index: number) =>
						isEditing ? (
							<Select
								showSearch
								value={reasonCode}
								labelInValue
								labelRender={(label) => `${label.value}`}
								onChange={(newValue) => handleReasonCodeChange(index, newValue)}
								options={reasonOptions}
								placeholder='Select reason code'
								style={{ width: 'calc(100% - 30px)' }}
								disabled={!record?.group}
							/>
						) : (
							<>
								<Typography.Text style={{ marginRight: 5 }}>{reasonCode && `${reasonCode.value}`}</Typography.Text>
								{reasonCode?.label && (
									<Popover content={reasonCode.label} styles={{ body: { maxWidth: 400 } }}>
										<Info />
									</Popover>
								)}
							</>
						),
				},
				{
					title: 'Amount',
					dataIndex: 'amount',
					key: 'amount',
					flex: 2,
					width: '33%',
					render: (amount: number | null, __: AnyType, index: number) =>
						isEditing ? (
							<InputNumber
								value={amount !== 0 ? amount : null}
								onChange={(newValue) => handleAmountChange(index, newValue)}
								placeholder='0.00'
								style={{ width: '100%' }}
								prefix='$'
								precision={2}
							/>
						) : (
							<Typography.Text>{renderAmount(amount)}</Typography.Text>
						),
				},
				isEditing
					? {
							title: '',
							dataIndex: 'action',
							key: 'action',
							width: 50,
							render: (__: AnyType, ___: AnyType, index: number) => (
								<Button type='link' danger onClick={() => removeItem(index)} icon={<DeleteOne />} size='small' />
							),
						}
					: null,
			].filter(Boolean),
		[handleAmountChange, handleGroupChange, handleReasonCodeChange, isEditing, removeItem],
	);

	return (
		<div>
			<Table
				bordered={false}
				columns={columns as AnyType}
				dataSource={dataSource}
				className={'custom-line-adjustments-table'}
				pagination={false}
				size='small'
				style={{ margin: 0 }}
			/>
			{isEditing && (
				<Button type='link' onClick={addItem} icon={<Plus />} style={{ width: 'auto', margin: 0, marginBottom: 4 }}>
					{'Add Line Adjustment'}
				</Button>
			)}
			<Divider style={{ margin: '0', borderColor: '#d4e6ff' }} />
		</div>
	);
};

export default CustomLineAdjustmentsRenderer;
